import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

export type ThemeMode = 'light' | 'dark' | 'system';

export interface PageSettings {
  sidebarCollapsed: boolean;
  themeMode: ThemeMode;
  showBreadcrumb: boolean;
  pageTransition: boolean;
  pageSize: number;
  language: string;
  timeFormat: string;
  timezone: string;
  notifyOnError: boolean;
  notifyOnQuota: boolean;
  soundEnabled: boolean;
}

function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'Asia/Shanghai';
  }
}

const DEFAULT_SETTINGS: PageSettings = {
  sidebarCollapsed: false,
  themeMode: 'light',
  showBreadcrumb: true,
  pageTransition: true,
  pageSize: 20,
  language: 'zh-CN',
  timeFormat: '24h',
  timezone: detectTimezone(),
  notifyOnError: true,
  notifyOnQuota: true,
  soundEnabled: false,
};

const STORAGE_KEY = 'aigatewayx_page_settings';

function readSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Resolve whether the dark class should be applied given the user's mode and the OS preference. */
export function resolveDark(mode: ThemeMode): boolean {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  return readSystemPrefersDark();
}

/** Toggle Element Plus dark theme by switching the `dark` class on <html>. */
export function applyDarkMode(enabled: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', enabled);
}

/** Apply the theme based on the resolved mode (system included). */
export function applyThemeMode(mode: ThemeMode): void {
  applyDarkMode(resolveDark(mode));
}

interface LegacyShape {
  darkMode?: boolean;
  [k: string]: unknown;
}

function loadFromStorage(): Partial<PageSettings> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as LegacyShape;
      // Migration: older builds stored a boolean `darkMode` instead of `themeMode`.
      if (parsed && parsed.themeMode === undefined && typeof parsed.darkMode === 'boolean') {
        parsed.themeMode = parsed.darkMode ? 'dark' : 'light';
      }
      return parsed as Partial<PageSettings>;
    }
  } catch {
    /* ignore malformed storage */
  }
  return {};
}

/**
 * Apply persisted theme before the app mounts to avoid a light-mode flash.
 * Safe to call before Pinia is initialised — reads localStorage directly.
 */
export function preloadTheme(): void {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;
    const parsed = JSON.parse(saved) as LegacyShape;
    const mode: ThemeMode = ((): ThemeMode => {
      if (parsed.themeMode === 'light' || parsed.themeMode === 'dark' || parsed.themeMode === 'system') {
        return parsed.themeMode;
      }
      if (typeof parsed.darkMode === 'boolean') return parsed.darkMode ? 'dark' : 'light';
      return 'light';
    })();
    applyThemeMode(mode);
  } catch {
    /* ignore */
  }
}

/** Backwards-compatible alias for legacy callers (returns the legacy boolean). */
export function preloadDarkMode(): void {
  preloadTheme();
}

let systemMediaQuery: MediaQueryList | null = null;
let systemListener: ((e: MediaQueryListEvent) => void) | null = null;

function ensureSystemListener(getMode: () => ThemeMode): void {
  if (typeof window === 'undefined' || !window.matchMedia) return;
  if (systemMediaQuery && systemListener) return;
  systemMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  systemListener = () => {
    if (getMode() === 'system') applyThemeMode('system');
  };
  if (systemMediaQuery.addEventListener) {
    systemMediaQuery.addEventListener('change', systemListener);
  } else if ((systemMediaQuery as any).addListener) {
    // Safari < 14 fallback
    (systemMediaQuery as any).addListener(systemListener);
  }
}

export const usePageSettingsStore = defineStore('page-settings', () => {
  const settings = ref<PageSettings>({ ...DEFAULT_SETTINGS, ...loadFromStorage() });

  function saveSettings(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings.value));
  }

  function update(patch: Partial<PageSettings>): void {
    Object.assign(settings.value, patch);
    saveSettings();
  }

  function resetSettings(): void {
    settings.value = { ...DEFAULT_SETTINGS };
    saveSettings();
  }

  // Keep <html.dark> in sync with the themeMode flag, and react to OS theme changes
  // when the user picked "follow system".
  watch(
    () => settings.value.themeMode,
    (mode) => {
      applyThemeMode(mode);
      ensureSystemListener(() => settings.value.themeMode);
    },
    { immediate: true },
  );

  return { settings, saveSettings, update, resetSettings };
});
